import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CheckoutPage.css';

/**
 * CheckoutPage - Complete booking with passenger info and payment
 * 
 * Flow:
 * 1. Review trip summary
 * 2. Enter passenger details
 * 3. Add payment method
 * 4. Confirm and book
 */
const CheckoutPage = () => {
  const navigate = useNavigate();
  
  // Trip data from TravelSearchPage
  const [tripData, setTripData] = useState(null);
  const [trialContext, setTrialContext] = useState(null);

  // Form state
  const [passengerInfo, setPassengerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
  });

  const [billingAddress, setBillingAddress] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load trip data on mount
  useEffect(() => {
    const storedTrip = sessionStorage.getItem('trip_data');
    const storedTrial = sessionStorage.getItem('ac_trial_context');

    if (!storedTrip) {
      // No trip data - redirect back to search
      alert('No trip data found. Please search for flights first.');
      navigate('/travel/search');
      return;
    }

    try {
      const trip = JSON.parse(storedTrip);
      setTripData(trip);
      
      if (storedTrial) {
        setTrialContext(JSON.parse(storedTrial));
      }
    } catch (e) {
      console.error('Failed to parse trip data:', e);
      navigate('/travel/search');
    }
  }, [navigate]);

  // Calculate totals
  const calculateTotals = () => {
    if (!tripData) return { subtotal: 0, taxes: 0, total: 0 };

    const flightTotal = (tripData.outboundFlight?.price || 0) + (tripData.returnFlight?.price || 0);
    const hotelTotal = tripData.hotel?.totalPrice || 0;
    const carTotal = tripData.car?.totalPrice || 0;

    const subtotal = flightTotal + hotelTotal + carTotal;
    const taxes = subtotal * 0.08; // 8% estimated taxes
    const total = subtotal + taxes;

    return { subtotal, taxes, total, flightTotal, hotelTotal, carTotal };
  };

  const totals = calculateTotals();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!termsAccepted) {
      alert('Please accept the terms and conditions');
      return;
    }

    setLoading(true);

    // TODO: Process payment with Stripe
    // TODO: Create booking in backend
    // TODO: Send confirmation email

    // For now, just show success message
    setTimeout(() => {
      alert('Booking successful! (Payment integration coming next)');
      
      // Clear all travel-related session data on successful booking
      sessionStorage.removeItem('trip_data');
      sessionStorage.removeItem('travel_search_state');
      sessionStorage.removeItem('ac_trial_context');
      
      navigate('/');
    }, 2000);
  };

  if (!tripData) {
    return (
      <div className="checkout-loading">
        <div className="loading-spinner"></div>
        <p>Loading checkout...</p>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        
        {/* Header */}
        <div className="checkout-header">
          <button className="back-button" onClick={() => {
            // DON'T clear travel_search_state - we want to preserve it!
            // Only clear trip_data if user wants to modify their selections
            navigate('/travel/search');
          }}>
            ← Back to Search
          </button>
          <h1>Complete Your Booking</h1>
          {trialContext && (
            <p className="trial-context-text">
              For: {trialContext.trialName}
            </p>
          )}
        </div>

        <div className="checkout-content">
          
          {/* Left Column - Forms */}
          <div className="checkout-forms">
            
            {/* Passenger Information */}
            <div className="checkout-section">
              <h2>Passenger Information</h2>
              <form className="passenger-form">
                <div className="form-row">
                  <div className="form-field">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={passengerInfo.firstName}
                      onChange={(e) => setPassengerInfo({...passengerInfo, firstName: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={passengerInfo.lastName}
                      onChange={(e) => setPassengerInfo({...passengerInfo, lastName: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={passengerInfo.email}
                      onChange={(e) => setPassengerInfo({...passengerInfo, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={passengerInfo.phone}
                      onChange={(e) => setPassengerInfo({...passengerInfo, phone: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    value={passengerInfo.dateOfBirth}
                    onChange={(e) => setPassengerInfo({...passengerInfo, dateOfBirth: e.target.value})}
                    required
                  />
                </div>
              </form>
            </div>

            {/* Billing Address */}
            <div className="checkout-section">
              <h2>Billing Address</h2>
              <form className="billing-form">
                <div className="form-field">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    value={billingAddress.address}
                    onChange={(e) => setBillingAddress({...billingAddress, address: e.target.value})}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>City *</label>
                    <input
                      type="text"
                      value={billingAddress.city}
                      onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>State *</label>
                    <input
                      type="text"
                      value={billingAddress.state}
                      onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Zip Code *</label>
                    <input
                      type="text"
                      value={billingAddress.zipCode}
                      onChange={(e) => setBillingAddress({...billingAddress, zipCode: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Payment Method */}
            <div className="checkout-section">
              <h2>Payment Method</h2>
              <div className="payment-placeholder">
                <div className="stripe-card-element">
                  {/* Stripe card element will go here */}
                  <p className="payment-note">💳 Stripe payment integration coming next...</p>
                </div>
              </div>
            </div>

            {/* Terms and Submit */}
            <div className="checkout-section">
              <label className="terms-checkbox">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>
                  I accept the terms and conditions and privacy policy
                </span>
              </label>

              <button
                className="complete-booking-button"
                onClick={handleSubmit}
                disabled={loading || !termsAccepted}
              >
                {loading ? 'Processing...' : `Complete Booking - $${totals.total.toFixed(2)}`}
              </button>
            </div>
          </div>

          {/* Right Column - Trip Summary */}
          <div className="checkout-summary">
            <div className="summary-card">
              <h2>Trip Summary</h2>

              {/* Outbound Flight */}
              {tripData.outboundFlight && (
                <div className="summary-item">
                  <div className="summary-item-header">
                    <span className="summary-icon">✈️</span>
                    <span className="summary-label">Outbound Flight</span>
                  </div>
                  <div className="summary-item-details">
                    <p>{tripData.outboundFlight.origin} → {tripData.outboundFlight.destination}</p>
                    <p className="summary-meta">
                      {tripData.outboundFlight.airline} • {tripData.outboundFlight.flightNumber}
                    </p>
                  </div>
                  <div className="summary-item-price">${tripData.outboundFlight.price}</div>
                </div>
              )}

              {/* Return Flight */}
              {tripData.returnFlight && (
                <div className="summary-item">
                  <div className="summary-item-header">
                    <span className="summary-icon">✈️</span>
                    <span className="summary-label">Return Flight</span>
                  </div>
                  <div className="summary-item-details">
                    <p>{tripData.returnFlight.origin} → {tripData.returnFlight.destination}</p>
                    <p className="summary-meta">
                      {tripData.returnFlight.airline} • {tripData.returnFlight.flightNumber}
                    </p>
                  </div>
                  <div className="summary-item-price">${tripData.returnFlight.price}</div>
                </div>
              )}

              {/* Hotel */}
              {tripData.hotel && (
                <div className="summary-item">
                  <div className="summary-item-header">
                    <span className="summary-icon">🏨</span>
                    <span className="summary-label">Hotel</span>
                  </div>
                  <div className="summary-item-details">
                    <p>{tripData.hotel.name}</p>
                    <p className="summary-meta">
                      {tripData.hotel.checkIn} - {tripData.hotel.checkOut}
                    </p>
                  </div>
                  <div className="summary-item-price">${tripData.hotel.totalPrice}</div>
                </div>
              )}

              {/* Car Rental */}
              {tripData.car && (
                <div className="summary-item">
                  <div className="summary-item-header">
                    <span className="summary-icon">🚗</span>
                    <span className="summary-label">Rental Car</span>
                  </div>
                  <div className="summary-item-details">
                    <p>{tripData.car.carCategory} - {tripData.car.carModel}</p>
                    <p className="summary-meta">
                      {tripData.car.provider}
                    </p>
                  </div>
                  <div className="summary-item-price">${tripData.car.totalPrice}</div>
                </div>
              )}

              {/* Price Breakdown */}
              <div className="price-breakdown">
                <div className="price-row">
                  <span>Subtotal</span>
                  <span>${totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="price-row">
                  <span>Taxes & Fees</span>
                  <span>${totals.taxes.toFixed(2)}</span>
                </div>
                <div className="price-row total-row">
                  <span>Total</span>
                  <span>${totals.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="trust-badges">
                <div className="trust-badge">
                  <span className="trust-icon">🔒</span>
                  <span className="trust-text">Secure Payment</span>
                </div>
                <div className="trust-badge">
                  <span className="trust-icon">✓</span>
                  <span className="trust-text">HIPAA Compliant</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;